import './App.css'

import { CheckCircleTwoTone } from '@mui/icons-material'
import { Box, CssBaseline, Typography } from '@mui/material'
import { ConfirmProvider } from 'material-ui-confirm'
import { useEffect } from 'react'
import { useRoutes } from 'react-router-dom'

import ReloadPrompt from './app/base/components/ReloadPrompt/ReloadPrompt'
import MatxTheme from './app/base/components/Template/MatxTheme/MatxTheme'
import { AppConfirmProvider } from './app/base/contexts/AppConfirmProvider.tsx'
import { BreadcrumbProvider } from './app/base/contexts/BreadcrumbContext.tsx'
import { ErrorProvider } from './app/base/contexts/ErrorProvider.tsx'
import { AuthProvider } from './app/base/contexts/JWTAuthContext'
import { SecurityProvider } from './app/base/contexts/SecurityContext.tsx'
import { SettingsProvider } from './app/base/contexts/SettingsContext.tsx'
import { ToastProvider } from './app/base/contexts/ToastContext.tsx'
import { appRoutes } from './app/routes/routes'

/**
 * @author isi-template
 * @constructor
 */
function App() {
  const content = useRoutes(appRoutes)
  const link = document.querySelector('link[rel="icon"]')

  const isDebugMode = import.meta.env.APP_ENV === 'local'

  const version = import.meta.env.ISI_VERSION || '--'

  const appName = import.meta.env.ISI_TITLE || 'ISI-PAY'

  useEffect(() => {
    if (link) {
      if (import.meta.env.ISI_FAVICON) {
        link.setAttribute('href', import.meta.env.ISI_FAVICON)
      }
    }
    console.clear()
    const message = `%c ¡Atención! Estás utilizando la aplicación para ${appName}. Versión: ${version}`
    const styles = [
      'color: #ff9800', // Naranja Amber
      'font-size: 1.2em',
      'font-weight: bold',
    ].join(';')
    console.log(message, styles)
  }, [version, link, appName])

  return (
    <SettingsProvider>
      <AuthProvider>
        <BreadcrumbProvider>
          <SecurityProvider>
            <MatxTheme>
              <ToastProvider defaultOptions={{ position: 'center_top' }}>
                <ErrorProvider>
                  <AppConfirmProvider
                    defaultOptions={{
                      title: 'Autorización',
                      content: '¿Proceder con la solicitud?',
                      confirmationText: 'Aceptar',
                      cancellationText: 'Cancelar',
                      showInput: false,
                      disableCloseOnOverlay: false,
                    }}
                  >
                    <ConfirmProvider
                      defaultOptions={{
                        title: 'Confirmación',
                        content: '¿Proceder con la solicitud?',
                        allowClose: false,
                        confirmationText: 'Aceptar',
                        cancellationText: 'Cancelar',
                        dialogProps: {
                          fullWidth: true,
                          maxWidth: 'xs',
                          disableRestoreFocus: true,
                          disableEnforceFocus: true,
                          'aria-hidden': false,
                        },
                        titleProps: {
                          sx: {
                            py: 1.5,
                          },
                        },
                        contentProps: {
                          dividers: true,
                        },
                        dialogActionsProps: {
                          sx: { justifyContent: 'center' },
                        },
                        confirmationButtonProps: {
                          color: 'primary',
                          variant: 'contained',
                          size: 'medium',
                          startIcon: <CheckCircleTwoTone />,
                          'aria-label': 'confirm',
                          autoFocus: false,
                        },
                        cancellationButtonProps: {
                          color: 'error',
                          variant: 'text',
                          size: 'medium',
                          'aria-label': 'close',
                          autoFocus: false,
                        },
                        buttonOrder: ['cancel', 'confirm'],
                      }}
                    >
                      <CssBaseline />
                      {isDebugMode && (
                        <Box
                          sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 9999,
                            pointerEvents: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: {
                                xs: '140px',
                                sm: '180px',
                                md: '220px',
                              },
                              fontWeight: 100,
                              color: 'rgba(0, 0, 0, 0.03)',
                              textTransform: 'uppercase',
                              letterSpacing: '30px',
                              userSelect: 'none',
                              transform: 'rotate(-45deg)',
                            }}
                          >
                            SANDBOX
                          </Typography>
                        </Box>
                      )}
                      {content}
                      <ReloadPrompt />
                    </ConfirmProvider>
                  </AppConfirmProvider>
                </ErrorProvider>
              </ToastProvider>
            </MatxTheme>
          </SecurityProvider>
        </BreadcrumbProvider>
      </AuthProvider>
    </SettingsProvider>
  )
}

export default App
